import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaArrowRight } from 'react-icons/fa';
import Images from '../assets/AllImages';

// Rotating promos shown across the site to push visitors to the Offers page.
const PROMOS = [
  { img: Images.OfferShkollaVeroreChildren, key: 'offers.popup.kids' },
  { img: Images.OfferShkollaVeroreAdults, key: 'offers.popup.adults' },
  { img: Images.OfferPakoIndividuale, key: 'offers.popup.individual' },
  { img: Images.OfferHyrjaDitore, key: 'offers.popup.daily' },
];

const FIRST_DELAY = 3000;   // first popup after ~3s of browsing
const REPEAT_DELAY = 25000; // then rotate a new one every ~25s

export default function OfferPopup() {
  const { t } = useTranslation();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [index, setIndex] = useState(0);
  const hasShownRef = useRef(false);

  // Hide instantly when the visitor is already on the Offers page.
  useEffect(() => {
    if (location.pathname === '/ofertat') setVisible(false);
  }, [location.pathname]);

  // Schedule the next appearance whenever the popup is hidden.
  useEffect(() => {
    if (visible) return;
    const delay = hasShownRef.current ? REPEAT_DELAY : FIRST_DELAY;
    const timer = setTimeout(() => {
      if (window.location.pathname !== '/ofertat') {
        hasShownRef.current = true;
        setVisible(true);
      }
    }, delay);
    return () => clearTimeout(timer);
  }, [visible]);

  const close = () => {
    setVisible(false);
    setIndex((i) => (i + 1) % PROMOS.length); // next time show a different offer
  };

  if (!visible || location.pathname === '/ofertat') return null;

  const promo = PROMOS[index];

  return createPortal(
    <div
      className="offer-popup"
      style={{
        position: 'fixed',
        bottom: '18px',
        right: '18px',
        width: 'min(330px, calc(100vw - 32px))',
        backgroundColor: '#fff',
        borderRadius: '18px',
        overflow: 'hidden',
        boxShadow: '0 18px 50px rgba(8, 14, 40, 0.28)',
        zIndex: 4000,
      }}
    >
      {/* Image header */}
      <div style={{ position: 'relative' }}>
        <img
          src={promo.img}
          alt=""
          style={{ display: 'block', width: '100%', height: '130px', objectFit: 'cover', objectPosition: 'top center' }}
        />
        <span
          className="fw-bold text-uppercase"
          style={{
            position: 'absolute',
            top: '12px',
            left: '12px',
            backgroundColor: '#fff',
            color: '#1D3EE3',
            borderRadius: '999px',
            padding: '5px 12px',
            fontSize: '0.68rem',
            letterSpacing: '0.06em',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          }}
        >
          {t('offers.popup.badge')}
        </span>
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="d-flex align-items-center justify-content-center"
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            border: 'none',
            backgroundColor: 'rgba(8,14,40,0.55)',
            color: '#fff',
            cursor: 'pointer',
          }}
        >
          <FaTimes size={14} />
        </button>
      </div>

      {/* Body */}
      <div className="p-3">
        <p className="fw-bold mb-1" style={{ color: '#0f1c4d', fontSize: '1.02rem', lineHeight: 1.3 }}>
          {t(promo.key)}
        </p>
        <p className="mb-3" style={{ color: '#7c8298', fontSize: '0.85rem' }}>
          {t('offers.popup.deadline')}
        </p>
        <Link
          to="/ofertat"
          onClick={() => setVisible(false)}
          className="d-flex align-items-center justify-content-center gap-2 fw-bold text-white"
          style={{
            backgroundColor: '#1D3EE3',
            borderRadius: '12px',
            padding: '11px 16px',
            textDecoration: 'none',
            fontSize: '0.98rem',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#1530b8')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#1D3EE3')}
        >
          {t('offers.popup.cta')} <FaArrowRight size={13} />
        </Link>
      </div>
    </div>,
    document.body
  );
}
